// Every badge definition, grouped by family then category, with filters. A row
// opens the badge's panel (art, description, who holds it).
import React, { useMemo, useState } from "react";
import { TIERS } from "../types";
import { BadgeArt } from "../art";
import { familyAccent, fmtInt, fmtPoints, tierLabel } from "../collection/badgeFormat";
import {
  EMPTY_FILTERS,
  familyOptions,
  filterBadges,
  groupBadges,
  hasFilters,
  membersWithBadges,
  thresholdText,
  type CatalogueFilters,
} from "./adminModel";
import type { AdminBadge } from "./adminTypes";
import { Markers, HoldersCell, TierChip } from "./AdminBits";

function BadgeRow({ badge, onOpen }: { badge: AdminBadge; onOpen: (code: string) => void }) {
  return (
    <li>
      <button
        type="button"
        className={`badm-row${badge.active ? "" : " badm-row--inactive"}`}
        onClick={() => onOpen(badge.code)}
        data-code={badge.code}
        aria-label={`${badge.name}, ${badge.code}`}
      >
        <span className="badm-row__art">
          <BadgeArt
            category={badge.category}
            tier={badge.tier}
            number={badge.number}
            code={badge.code}
            name={badge.name}
            size={44}
            variant="full"
            locked={!badge.active}
          />
        </span>
        <span className="badm-row__id">
          <span className="badm-row__name">{badge.name}</span>
          <code className="badm-code">{badge.code}</code>
        </span>
        <span className="badm-row__meta">
          <span className="badm-row__tier">
            <TierChip tier={badge.tier} />
          </span>
          <span className="badm-row__pts" title="Points">
            {fmtPoints(badge.points)}
            <span className="badm-row__unit"> pts</span>
          </span>
          <span className="badm-row__thr" title="Threshold">
            {thresholdText(badge)}
          </span>
          <span className="badm-row__marks">
            <Markers badge={badge} />
          </span>
          <span className="badm-row__rarity">
            <HoldersCell badge={badge} />
          </span>
        </span>
      </button>
    </li>
  );
}

const CataloguePanel: React.FC<{
  badges: readonly AdminBadge[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpen: (code: string) => void;
}> = ({ badges, loading, error, onRetry, onOpen }) => {
  const [filters, setFilters] = useState<CatalogueFilters>(EMPTY_FILTERS);
  const set = <K extends keyof CatalogueFilters>(key: K, value: CatalogueFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const shown = useMemo(() => filterBadges(badges, filters), [badges, filters]);
  const groups = useMemo(() => groupBadges(shown), [shown]);
  const families = useMemo(() => familyOptions(badges), [badges]);
  const members = membersWithBadges(badges);
  const inactive = badges.filter((b) => !b.active).length;
  const uncounted = badges.filter((b) => !b.stats).length;
  const nobody = badges.filter((b) => b.stats && b.stats.holders === 0).length;

  let body: React.ReactNode;
  if (loading) {
    body = <p className="badm-muted">Loading the catalogue</p>;
  } else if (error) {
    body = (
      <div className="badm-inline-row">
        <p className="badm-muted">Could not load the badge definitions.</p>
        <button type="button" className="badm-btn badm-btn--sm" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  } else if (!badges.length) {
    body = (
      <p className="badm-note badm-note--warn">
        No badge definitions in this database. Run <code>pnpm badges:seed</code> in n450s_backend.
      </p>
    );
  } else {
    body = (
      <>
        <p className="badm-lead">
          {fmtInt(badges.length)} badges{inactive ? `, ${fmtInt(inactive)} inactive` : ""}.{" "}
          {members ? `${fmtInt(members)} members hold at least one. ` : ""}
          {nobody ? `${fmtInt(nobody)} nobody holds yet.` : ""}
          {uncounted
            ? ` ${fmtInt(uncounted)} have no holder counts yet; the next real sweep fills them in.`
            : ""}
        </p>

        <div className="badm-filters" role="search">
          <input
            type="search"
            className="badm-input badm-filters__q"
            placeholder="Search name, code or category"
            aria-label="Search badges"
            value={filters.q}
            onChange={(e) => set("q", e.target.value)}
          />
          <select
            className="badm-input"
            aria-label="Family"
            value={filters.family}
            onChange={(e) => set("family", e.target.value)}
          >
            <option value="">Every family</option>
            {families.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <select
            className="badm-input"
            aria-label="Tier"
            value={filters.tier}
            onChange={(e) => set("tier", e.target.value)}
          >
            <option value="">Every tier</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {tierLabel(t)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`badm-chip${filters.nobody ? " badm-chip--on" : ""}`}
            aria-pressed={filters.nobody}
            onClick={() => set("nobody", !filters.nobody)}
          >
            Nobody holds it
          </button>
          <button
            type="button"
            className={`badm-chip${filters.inactive ? " badm-chip--on" : ""}`}
            aria-pressed={filters.inactive}
            onClick={() => set("inactive", !filters.inactive)}
          >
            Inactive
          </button>
        </div>
        <p className="badm-filters__count" aria-live="polite">
          {hasFilters(filters) ? (
            <>
              Showing {fmtInt(shown.length)} of {fmtInt(badges.length)}.{" "}
              <button type="button" className="badm-link" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </button>
            </>
          ) : (
            `Showing all ${fmtInt(badges.length)}.`
          )}
        </p>

        {groups.length === 0 ? (
          <p className="badm-muted">No badge matches these filters.</p>
        ) : (
          <div className="badm-families">
            {groups.map((g) => (
              <section
                key={g.family}
                className="badm-family"
                style={{ "--badm-accent": familyAccent(g.family) } as React.CSSProperties}
                aria-label={g.label}
              >
                <header className="badm-family__head">
                  <h3 className="badm-family__title">{g.label}</h3>
                  <span className="badm-family__count">
                    {fmtInt(g.count)} {g.count === 1 ? "badge" : "badges"}
                  </span>
                </header>
                <div className="badm-cols" aria-hidden="true">
                  <span />
                  <span>Badge</span>
                  <span>Tier</span>
                  <span>Points</span>
                  <span>Threshold</span>
                  <span />
                  <span>Holders</span>
                </div>
                {g.categories.map((c) => (
                  <div key={c.category} className="badm-cat">
                    <h4 className="badm-cat__title">
                      <code>{c.category}</code>
                      <span className="badm-cat__count">{fmtInt(c.badges.length)}</span>
                    </h4>
                    <ul className="badm-rows">
                      {c.badges.map((b) => (
                        <BadgeRow key={b.id} badge={b} onOpen={onOpen} />
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <section className="badm-panel" aria-labelledby="badm-cat-title">
      <header className="badm-panel__head" style={{ background: "#22d6ff" }}>
        <h2 id="badm-cat-title" className="badm-panel__title">
          Catalogue
        </h2>
        <span className="badm-panel__aside">from the seed</span>
      </header>
      <div className="badm-panel__body">{body}</div>
    </section>
  );
};

export default CataloguePanel;
